import { ApiReferenceReact } from "@scalar/api-reference-react";
import "@scalar/api-reference-react/style.css";
import { Server, ShieldCheck } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import {
  Card,
  ErrorBlock,
  PageHeader,
  StatusBadge
} from "@/components/ui-ext/primitives";
import { AiIntegrationDialog } from "@/components/console/AiIntegrationDialog";
import { scalarCustomCss } from "@/components/console/ScalarTheme";
import { useAihrAuth } from "@/lib/auth/useAihrAuth";
import { OpenApiDocument, useOpenApiSpec } from "@/lib/spec/useOpenApiSpec";

const localSecurityHeaders = {
  cosmosTenantId: { value: "default-tenant" },
  cosmosUserId: { value: "local-cosmos-user" },
  cosmosRoles: { value: "admin,operator" }
};

const applyHeaders = (
  request: Request,
  requestBuilder: { headers?: Headers } | undefined,
  token: string | undefined,
  authEnabled: boolean
) => {
  const headers = requestBuilder?.headers ?? request.headers;
  if (token) {
    headers.set("authorization", `Bearer ${token}`);
    return;
  }
  if (!authEnabled) {
    headers.set("x-cosmos-tenant-id", "default-tenant");
    headers.set("x-cosmos-user-id", "local-cosmos-user");
    headers.set("x-cosmos-roles", "admin,operator");
  }
};

export function ConsolePage() {
  const spec = useOpenApiSpec();
  const auth = useAihrAuth();
  const [token, setToken] = useState<string>();

  useEffect(() => {
    let cancelled = false;
    if (!auth.authEnabled) {
      setToken(undefined);
      return;
    }
    void auth.getAccessToken().then((accessToken) => {
      if (!cancelled) {
        setToken(accessToken);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [auth]);

  const configuration = useMemo(
    () => ({
      content: spec.data as OpenApiDocument | undefined,
      theme: "default" as const,
      layout: "modern" as const,
      customCss: scalarCustomCss,
      hideClientButton: false,
      hideTestRequestButton: false,
      defaultOpenAllTags: true,
      showSidebar: true,
      showDeveloperTools: "always" as const,
      documentDownloadType: "json" as const,
      operationTitleSource: "summary" as const,
      withDefaultFonts: false,
      persistAuth: false,
      telemetry: false,
      servers: spec.data?.servers,
      authentication: {
        preferredSecurityScheme: auth.authEnabled
          ? "auth0Bearer"
          : [["cosmosTenantId", "cosmosUserId", "cosmosRoles"]],
        securitySchemes: auth.authEnabled
          ? {
              auth0Bearer: { token }
            }
          : localSecurityHeaders
      },
      onBeforeRequest: ({ request, requestBuilder }: {
        request: Request;
        requestBuilder?: { headers?: Headers };
      }) => applyHeaders(request, requestBuilder, token, auth.authEnabled)
    }),
    [auth.authEnabled, spec.data, token]
  );

  return (
    <div className="p-6">
      <PageHeader
        eyebrow="Console"
        title="API & MCP Console"
        subtitle="Scalar-powered interactive documentation for Cosmos REST endpoints and MCP capabilities."
        actions={
          <>
            <StatusBadge tone={auth.authEnabled ? "success" : "info"} dot>
              <ShieldCheck className="h-3 w-3" />
              {auth.authEnabled ? "Auth0" : "Local headers"}
            </StatusBadge>
            <StatusBadge tone="azure" dot>
              <Server className="h-3 w-3" />
              {spec.data?.servers?.[0]?.url ?? "Loading spec"}
            </StatusBadge>
            <AiIntegrationDialog />
          </>
        }
      />

      {spec.isLoading && (
        <Card className="p-8 text-sm text-muted-foreground">
          Loading Cosmos API reference...
        </Card>
      )}

      {spec.error && (
        <ErrorBlock
          message={
            spec.error instanceof Error
              ? spec.error.message
              : "Unable to load the Cosmos OpenAPI document."
          }
          onRetry={() => void spec.refetch()}
        />
      )}

      {spec.data && (
        <Card className="overflow-hidden">
          <div className="min-h-[calc(100vh-13rem)]">
            <ApiReferenceReact configuration={configuration} />
          </div>
        </Card>
      )}
    </div>
  );
}
